/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type Period from '@linc-technologies/linced-common/models/timetable/period';
import type SessionService from '@linc-technologies/linced-common/services/session';
import moment from 'moment';
import type Movement from 'linced-app/models/attendance/movement';
import type Date from 'linced-app/models/date';
import type Group from 'linced-app/models/group';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';
import type AttendanceCodesService from 'linced-app/services/attendance-codes';

// Proto: attendance.Record.
export default class Record extends Model {
  declare [Type]: 'attendance/record';

  @service
  declare attendanceCodes: AttendanceCodesService;

  @service
  declare session: SessionService;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('date', { async: true, inverse: null })
  declare dateId: AsyncBelongsTo<Date>;

  @attr('string')
  declare date: string | undefined; // Date as a YYYY-MM-DD string

  @alias('dateId')
  declare dateObj: AsyncBelongsTo<Date>;

  @alias('dateObj.id')
  declare date_id: string | null | undefined;

  @belongsTo('timetable/period', { async: true, inverse: null })
  declare periodId: AsyncBelongsTo<Period>;

  @alias('periodId')
  declare period: AsyncBelongsTo<Period>;

  @alias('period.id')
  declare period_id: string | null | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @belongsTo('group', { async: true, inverse: null })
  declare homeroomId: AsyncBelongsTo<Group>;

  @alias('homeroomId')
  declare homeroom: AsyncBelongsTo<Group>;

  @alias('homeroom.id')
  declare homeroom_id: string | null | undefined;

  @attr('string', { defaultValue: '' })
  declare code: string;

  @attr('string', { defaultValue: '' })
  declare notes: string;

  // dayName returns the name of the weekday for which the record applies.
  @computed('date')
  get dayName() {
    return moment(this.date).format('ddd');
  }

  // dayOfWeek returns the ISO day number of the weekday for which the record applies.
  @computed('date')
  get dayOfWeek() {
    return moment(this.date).format('E');
  }

  @attr('string')
  declare auditReason: string | undefined;

  @attr('number')
  declare createTime: number | undefined;

  // Pseudo-attributes - should only ever be set on an update; never received
  // from the back end.

  // reason provides a description as to why the attendance edit was made.
  @attr('string', { defaultValue: '' })
  declare reason: string;

  // effectiveFrom provides a timestamp from which the updated code applies.
  @attr('number')
  declare effectiveFrom: number | undefined;

  // movements provides a log of movements, for jurisdictions which track them.
  @fragmentArray('attendance/movement')
  declare movements: FragmentArray<Movement>;

  @computed('code')
  get status() {
    // TS-STANDIN(attendance-codes): service is JS, so truancyMap infers as {}.
    const truancyCode = (
      this.attendanceCodes.truancyMap as { [code: string]: string }
    )[this.code];

    switch (truancyCode) {
      case 'P':
        return 'present';
      case 'J':
        return 'justified';
      case 'U':
        return 'unjustified';
      case 'O':
        return 'overseas';
    }
  }

  @computed(
    'movements.@each.{code,time}',
    'session.school.jurisdiction',
    'attendanceCodes.{truancyMap,medicalAbsence}',
  )
  get hasMinorPartialAbsence() {
    const jurisdiction = this.get('session.school.jurisdiction');
    if (jurisdiction !== Jurisdiction.AU_NSW) {
      return false;
    }
    const movements = this.movements;
    if (!movements || isEmpty(movements)) {
      return false;
    }

    // TS-STANDIN(attendance-codes): service is JS, so truancyMap infers as {}.
    const truancyMap =
      (this.attendanceCodes.truancyMap as { [code: string]: string }) || {};
    const medicalAbsence = this.get('attendanceCodes.medicalAbsence') as
      { code: string } | undefined;
    const medicalAbsenceCode = medicalAbsence && medicalAbsence.code;

    const orderedMovements = movements
      .slice()
      .sort((a, b) => (a.time || 0) - (b.time || 0));

    const absenceDurations: number[] = [];
    orderedMovements.forEach((movement, index) => {
      const startTime = movement.time;
      let truancy = truancyMap[movement.code];
      if (!truancy && movement.code === medicalAbsenceCode) {
        truancy = 'J';
      }
      if ((truancy !== 'U' && truancy !== 'J') || !startTime) {
        return;
      }
      const closingMovement = orderedMovements
        .slice(index + 1)
        .find((candidate) => {
          const candidateTruancy = truancyMap[candidate.code];
          return candidateTruancy === 'P' && candidate.time;
        });
      if (!closingMovement) {
        return;
      }
      const duration = (closingMovement.time - startTime) / 60;
      if (duration > 0) {
        absenceDurations.push(duration);
      }
    });

    if (isEmpty(absenceDurations)) {
      return false;
    }

    const totalMinutes = absenceDurations.reduce(
      (sum, duration) => sum + duration,
      0,
    );

    return totalMinutes > 0 && totalMinutes < 120;
  }
}
